import React, { useEffect, useState, useCallback } from "react";
import { FaRupeeSign, FaCalendarAlt } from "react-icons/fa";
import SummaryApi from "../common";
import { useAuth } from "../helpers/AuthContext";
import nopurchases from "../asset/nopurchases.png";

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const InvoiceModal = ({ isOpen, onClose, enrollment }) => {
  const invoiceRef = React.useRef();

  const todayDate = new Date().toISOString().split('T')[0];

  const safePrice = (price) => {
    if (!price && price !== 0) return 0;
    const num = parseFloat(String(price).replace(/[^0-9.-]+/g, ''));
    return isNaN(num) ? 0 : num;
  };

  const invoiceData = enrollment ? {
    invoiceDate: todayDate,
    status: 'Paid',
    paymentMode: 'Cash',
    from: {
      company: 'Skillara',
      address: '4th Floor, MKR Enclave, kamayyathopu',
      city: 'Kanuru, Vijayawada, Andhra Pradesh 520007',
      email: 'contact@skillara.com',
      phone: '+91 8335643321'
    },
    to: {
      name: `${enrollment.student_first_name || ''} ${enrollment.student_last_name || ''}`.trim() || 'N/A',
      address: enrollment.student_address || 'N/A',
      email: enrollment.email || 'N/A',
    },
    course: enrollment.course_title || 'N/A',
    quantity: 1,
    amount: safePrice(enrollment.course_price),
  } : null;

  if (!isOpen || !enrollment || !invoiceData) return null;

  const totalAmount = invoiceData.amount;

  const printInvoice = () => {
    const printContent = invoiceRef.current;
    // Open window slightly larger for better preview
    const printWindow = window.open('', '_blank', 'width=1000,height=800');
    
    const contentClone = printContent.cloneNode(true);
    
    // CSS to force desktop layout for printing even if clicked from mobile
    const printStyles = `
      <style>
        @media print {
          @page { size: A4; margin: 0; }
          body { 
            margin: 0 !important; 
            padding: 0 !important; 
            background: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .invoice-container { 
            width: 100% !important;
            max-width: 210mm !important;
            margin: 0 auto !important; 
            padding: 20mm !important; 
            background: white !important;
            box-shadow: none !important;
          }
          /* Force grid layouts for print */
          .print-grid-2 { display: grid !important; grid-template-columns: repeat(2, 1fr) !important; gap: 3rem !important; }
          .print-grid-3 { display: grid !important; grid-template-columns: repeat(3, 1fr) !important; gap: 2rem !important; }
          .print-flex-row { display: flex !important; flex-direction: row !important; justify-content: space-between !important; }
          .print-text-left { text-align: left !important; }
          .print-text-right { text-align: right !important; }
          .no-print { display: none !important; }
        }
        /* Base styles for the popup window */
        body {
          margin: 0;
          padding: 20px;
          background: #f3f4f6;
          font-family: system-ui, -apple-system, sans-serif;
        }
        .invoice-container {
          background: white;
          max-width: 210mm;
          margin: 0 auto;
          border-radius: 8px;
        }
      </style>
    `;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Invoice - ${enrollment.enrollment_id}</title>
        <script src="https://cdn.tailwindcss.com"></script>
        ${printStyles}
      </head>
      <body>
        <div class="invoice-container">
          ${contentClone.innerHTML}
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();

    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 500);
    };
  };

  const downloadPDF = async () => {
    try {
      const element = invoiceRef.current;
      
      await new Promise(resolve => {
        if (document.readyState === 'complete') resolve();
        else window.addEventListener('load', resolve);
      });

      const canvas = await html2canvas(element, {
        scale: 2, 
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        // CRITICAL FOR MOBILE: Force the canvas to render as if it's on a desktop (1200px wide)
        // This ensures the PDF looks like the desktop version even if downloaded from a phone.
        windowWidth: 1200, 
        onclone: (clonedDoc) => {
          clonedDoc.documentElement.classList.remove('dark');
          clonedDoc.body.classList.remove('dark');

          const clonedElement = clonedDoc.getElementById('invoice-content');
          if (clonedElement) {
             // Force desktop styling on the clone
             clonedElement.style.padding = "40px"; 
             const grids = clonedElement.querySelectorAll('.md\\:grid-cols-2');
             grids.forEach(g => g.style.gridTemplateColumns = 'repeat(2, minmax(0, 1fr))');
          }
        }
      });

      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = imgWidth / imgHeight;
      
      let width = pdfWidth;
      let height = width / ratio;
      
      if (height > pdfHeight) {
        height = pdfHeight;
        width = height * ratio;
      }
      
      const x = (pdfWidth - width) / 2;
      const y = (pdfHeight - height) / 2; // Centers vertical if small, or top align
      
      pdf.addImage(imgData, 'PNG', x, 0, width, height); // Align top usually better for invoice
      pdf.save(`Invoice_${enrollment.enrollment_id}_${todayDate}.pdf`);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-2 sm:p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-900 rounded-xl w-full max-w-4xl flex flex-col relative shadow-2xl my-auto max-h-[95vh]">

        {/* Close Icon */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 sm:top-4 sm:right-4 z-50 w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center transition no-print"
        >
          <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Action Buttons */}
        <div className="p-4 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex flex-wrap justify-center gap-3 no-print rounded-t-xl">
          <button 
            onClick={printInvoice} 
            className="flex-1 sm:flex-none justify-center px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white text-sm sm:text-base rounded-lg font-medium transition flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print
          </button>
          <button 
            onClick={downloadPDF} 
            className="flex-1 sm:flex-none justify-center px-4 py-3 bg-green-600 hover:bg-green-700 text-white text-sm sm:text-base rounded-lg font-medium transition flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            PDF
          </button>
        </div>

        {/* Invoice Content Wrapper (Scrollable) */}
        <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-800 rounded-b-xl">
          <div className="p-2 sm:p-6 md:p-8"> {/* Responsive Padding */}
            <div 
              ref={invoiceRef} 
              id="invoice-content"
              className="bg-white dark:bg-gray-900 rounded-lg shadow-sm p-4 sm:p-6 md:p-10 text-gray-800 dark:text-gray-100 print-preserve-colors mx-auto max-w-3xl"
            >

              {/* Header */}
              {/* Added print-flex-row to force horizontal layout on print even if mobile view is vertical */}
    <div className="bg-[#1C3664] text-white p-5 sm:p-8 md:p-10 rounded-t-lg -m-4 sm:-m-6  mb-6 sm:mb-8 print-preserve-colors print-flex-row flex flex-col md:flex-row justify-between items-start gap-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold">{invoiceData.from.company}</h1>
                  <p className="text-blue-100 mt-1 sm:mt-2 text-sm sm:text-lg">Professional Training Institute</p>
                </div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold self-start md:self-auto">INVOICE</h2>
              </div>

              {/* From & Bill To */}
              {/* Added print-grid-2 to force 2 columns on print */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-12 mb-6 sm:mb-10 print-grid-2">
                <div>
                  <h4 className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase mb-2 sm:mb-3">FROM</h4>
                  <p className="font-bold text-base sm:text-lg">{invoiceData.from.company}</p>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">{invoiceData.from.address}</p>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">{invoiceData.from.city}</p>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 break-all">{invoiceData.from.email}</p>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">{invoiceData.from.phone}</p>
                </div>
                <div>
                  <h4 className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase mb-2 sm:mb-3">BILL TO</h4>
                  <p className="font-bold text-base sm:text-lg">{invoiceData.to.name}</p>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 whitespace-pre-wrap">{invoiceData.to.address}</p>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 break-all">{invoiceData.to.email}</p>
                </div>
              </div>

              {/* Invoice Info */}
              {/* Added print-grid-3 to force 3 columns on print */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8 mb-6 sm:mb-10 p-4 sm:p-6 bg-gray-50 dark:bg-gray-800 rounded-lg print-preserve-colors dark:bg-gray-900 dark:text-gray-100 print-grid-3">
                <div className="flex flex-row sm:flex-col justify-between sm:justify-start items-center sm:items-start">
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">Invoice Date</p>
                  <p className="font-bold text-base sm:text-lg">{invoiceData.invoiceDate}</p>
                </div>
                <div className="flex flex-row sm:flex-col justify-between sm:justify-start items-center sm:items-start">
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">Status</p>
                  <span className="inline-block mt-0 sm:mt-1 px-3 sm:px-4 py-0.5 sm:py-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-full font-bold text-[10px] sm:text-sm print-preserve-colors" style={{ backgroundColor: '#dcfce7', color: '#166534' }}>
                    {invoiceData.status}
                  </span>
                </div>
                <div className="flex flex-row sm:flex-col justify-between sm:justify-start items-center sm:items-start">
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">Payment Mode</p>
                  <p className="font-bold text-green-600 dark:text-green-400 text-base sm:text-lg" style={{ color: '#16a34a' }}>Cash</p>
                </div>
              </div>

              {/* Course Table */}
              <div className="mb-6 sm:mb-12">
                <h4 className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase mb-3 sm:mb-5">COURSE PURCHASED</h4>
                <div className="overflow-x-auto border border-gray-300 dark:border-gray-700 rounded-lg">
                  <table className="w-full min-w-[500px] md:min-w-full">
                    <thead className="bg-gray-100 dark:bg-gray-800 print-preserve-colors dark:bg-gray-900 dark:text-gray-100">
                      <tr>
                        <th className="text-left py-3 sm:py-4 px-4 sm:px-6 font-bold text-gray-700 dark:text-gray-300 text-sm sm:text-base">Course Name</th>
                        <th className="text-center py-3 sm:py-4 px-4 sm:px-6 font-bold text-gray-700 dark:text-gray-300 text-sm sm:text-base">Qty</th>
                        <th className="text-right py-3 sm:py-4 px-4 sm:px-6 font-bold text-gray-700 dark:text-gray-300 text-sm sm:text-base">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t dark:border-gray-700">
                        <td className="py-4 sm:py-5 px-4 sm:px-6 font-medium text-sm sm:text-base">{invoiceData.course}</td>
                        <td className="py-4 sm:py-5 px-4 sm:px-6 text-center text-sm sm:text-base">{invoiceData.quantity}</td>
                        <td className="py-4 sm:py-5 px-4 sm:px-6 text-right font-bold text-sm sm:text-base">₹{totalAmount.toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Total */}
              <div className="flex justify-end">
                <div className="w-full md:w-96 bg-gray-100 dark:bg-gray-800 p-4 sm:p-6 rounded-lg border-2 border-blue-600 dark:border-blue-500 print-preserve-colors dark:bg-gray-900 dark:text-gray-100">
                  <div className="flex justify-between text-lg sm:text-2xl font-bold">
                    <span>Total Amount</span>
                    <span className="text-blue-600 dark:text-blue-400" style={{ color: '#2563eb' }}>₹{totalAmount.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-8 sm:mt-16 pt-6 sm:pt-8 border-t border-gray-300 dark:border-gray-700 text-center text-gray-500 dark:text-gray-400 text-[10px] sm:text-sm">
                <p className="font-bold">Thank you for choosing Skillara!</p>
                <p className="mt-2 break-words">{invoiceData.from.company} • {invoiceData.from.email} • {invoiceData.from.phone}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StudentPurchases = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { studentId } = useAuth();

  const fetchPurchasedCourses = useCallback(async () => {
    if (!studentId) {
      setEnrollments([]);
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(SummaryApi.student_purchased_courses.url, {
        method: SummaryApi.student_purchased_courses.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ student_id: studentId }),
      });

      const data = await res.json();
      if (data.status && Array.isArray(data.data)) {
        setEnrollments(data.data);
      } else {
        setEnrollments([]);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      setEnrollments([]);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    fetchPurchasedCourses();
  }, [fetchPurchasedCourses]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "Invalid";
    }
  };

  const formatPrice = (price) => {
    return parseFloat(price).toLocaleString("en-IN");
  };

  const handleViewBill = (enrollment) => {
    setSelectedEnrollment(enrollment);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedEnrollment(null);
  };

  if (loading) {
    return (
      <div className="p-6 min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#1f4774] dark:border-blue-500 border-t-transparent mx-auto"></div>
          <p className="mt-3 text-gray-600 dark:text-gray-300">Loading your purchases...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900">
      <div className="max-w-7xl mx-auto">

        {/* Page Title */}
        <h1 className="text-2xl sm:text-3xl font-bold text-[#1f4774] dark:text-white mb-6 text-center">
          My Purchases
        </h1>

        {/* No Enrollments */}
        {enrollments.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            <img
              src={nopurchases}
              alt="No Purchases"
              className="w-48 sm:w-64 h-auto object-contain mb-6 opacity-80"
            />
            <p className="text-lg font-medium text-gray-700 dark:text-gray-300">No courses purchased yet.</p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">Enroll in a course to see it here.</p>
          </div>
        ) : (
          /* Responsive List for Mobile, Table for Desktop */
          <div className="w-full">
            {/* Desktop Table (Hidden on Mobile) */}
            <div className="hidden md:block overflow-x-auto shadow-md rounded-xl border border-gray-200 dark:border-gray-700 mx-0">
              <table className="min-w-full bg-white dark:bg-gray-800 w-full">
                <thead>
                  <tr className="bg-[#1f4774] dark:bg-blue-700 text-white text-left text-sm font-semibold">
                    <th className="px-6 py-4">Course Name</th>
                    <th className="px-6 py-4 text-center">Enrolled Date</th>
                    <th className="px-6 py-4 text-center">Course Price</th>
                    <th className="px-6 py-4 text-center"></th>
                  </tr>
                </thead>
                <tbody className="text-gray-700 dark:text-gray-300">
                  {enrollments.map((enrollment) => (
                    <tr
                      key={enrollment.enrollment_id}
                      className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <span className="font-semibold text-[#1f4774] dark:text-white">{enrollment.course_title}</span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <FaCalendarAlt className="text-[#1f4774] dark:text-blue-400 text-sm" />
                          <span className="font-medium">{formatDate(enrollment.enrollment_date)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <FaRupeeSign className="text-[#1f4774] dark:text-green-400" />
                          <span className="font-bold text-lg">{formatPrice(enrollment.course_price)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          className="bg-[#1f4774] dark:bg-blue-600 hover:bg-blue-700 dark:hover:bg-blue-700 text-white font-medium px-5 py-3 rounded-lg transition-all flex items-center justify-center gap-1.5 mx-auto"
                          onClick={() => handleViewBill(enrollment)}
                        >
                          View Bill
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards (Visible only on Mobile) */}
            <div className="md:hidden space-y-4">
              {enrollments.map((enrollment) => (
                <div key={enrollment.enrollment_id} className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-bold text-[#1f4774] dark:text-blue-400 text-lg pr-4">{enrollment.course_title}</h3>
                    <div className="flex items-center text-green-600 dark:text-green-400 font-bold">
                       <FaRupeeSign className="text-sm" />
                       <span>{formatPrice(enrollment.course_price)}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center text-gray-600 dark:text-gray-400 text-sm mb-4">
                    <FaCalendarAlt className="mr-2" />
                    <span>Enrolled: {formatDate(enrollment.enrollment_date)}</span>
                  </div>

                  <button
                    className="w-full bg-[#1f4774] dark:bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-opacity-90 transition"
                    onClick={() => handleViewBill(enrollment)}
                  >
                    View Bill
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Invoice Modal */}
      <InvoiceModal 
        isOpen={isModalOpen} 
        onClose={closeModal} 
        enrollment={selectedEnrollment} 
      />
    </div>
  );
};

export default StudentPurchases;